import { ToolItem } from '../types';

export interface SocialToolItem extends ToolItem {
  socialCategory: 'youtube' | 'instagram-tiktok' | 'text-formatters' | 'emoji-helpers';
  socialCategoryLabel: string;
  iconBgColor: string;
  iconTextColor: string;
  iconName: string;
}

export const SOCIAL_CATEGORIES_TABS = [
  { id: 'all', label: 'All Social Media Tools', icon: 'Share2' },
  { id: 'youtube', label: 'YouTube Tools', icon: 'Youtube' },
  { id: 'instagram-tiktok', label: 'Instagram & TikTok', icon: 'Instagram' },
  { id: 'text-formatters', label: 'Posts & Formatting', icon: 'MessageSquare' },
  { id: 'emoji-helpers', label: 'Emoji & Utilities', icon: 'Smile' }
];

export const SOCIAL_TOOLS_DATA: SocialToolItem[] = [
  // --- YOUTUBE TOOLS (8 Tools) ---
  {
    id: 'youtube-thumbnail-preview',
    name: 'YouTube Thumbnail Preview',
    description: 'Preview how your thumbnail graphic looks across YouTube homefeed, sidebar, and mobile screen sizes.',
    socialCategory: 'youtube',
    socialCategoryLabel: 'YouTube Tools',
    iconBgColor: 'bg-red-500/15',
    iconTextColor: 'text-red-500',
    iconName: 'Youtube',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'youtube-thumbnail-safe-area',
    name: 'YouTube Thumbnail Safe Area',
    description: 'Check thumbnail safe zones to avoid text overlay obstruction from timestamp badges and UI buttons.',
    socialCategory: 'youtube',
    socialCategoryLabel: 'YouTube Tools',
    iconBgColor: 'bg-rose-500/15',
    iconTextColor: 'text-rose-500',
    iconName: 'Maximize2',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'youtube-banner-safe-area',
    name: 'YouTube Banner Safe Area',
    description: 'Overlay desktop, tablet, TV, and mobile display dimensions to position your banner graphics perfectly.',
    socialCategory: 'youtube',
    socialCategoryLabel: 'YouTube Tools',
    iconBgColor: 'bg-orange-500/15',
    iconTextColor: 'text-orange-500',
    iconName: 'Monitor',
    actionType: 'generic'
  },
  {
    id: 'youtube-title-counter',
    name: 'YouTube Title Counter',
    description: 'Optimize video title length within YouTube\'s 100 character limit (60 max recommended for mobile).',
    socialCategory: 'youtube',
    socialCategoryLabel: 'YouTube Tools',
    iconBgColor: 'bg-amber-500/15',
    iconTextColor: 'text-amber-500',
    iconName: 'Type',
    actionType: 'generic'
  },
  {
    id: 'youtube-description-counter',
    name: 'YouTube Description Counter',
    description: 'Format video descriptions, monitor 5000 character limits, and optimize above-the-fold snippet info.',
    socialCategory: 'youtube',
    socialCategoryLabel: 'YouTube Tools',
    iconBgColor: 'bg-emerald-500/15',
    iconTextColor: 'text-emerald-500',
    iconName: 'FileText',
    actionType: 'generic'
  },
  {
    id: 'youtube-tag-formatter',
    name: 'YouTube Tag Formatter',
    description: 'Format tag keywords with commas and validate total tag character count within YouTube\'s 500 limit.',
    socialCategory: 'youtube',
    socialCategoryLabel: 'YouTube Tools',
    iconBgColor: 'bg-teal-500/15',
    iconTextColor: 'text-teal-500',
    iconName: 'Hash',
    actionType: 'generic'
  },
  {
    id: 'youtube-timestamp-generator',
    name: 'YouTube Timestamp Generator',
    description: 'Generate clickable video timestamps (00:00) and chapter list descriptions effortlessly.',
    socialCategory: 'youtube',
    socialCategoryLabel: 'YouTube Tools',
    iconBgColor: 'bg-cyan-500/15',
    iconTextColor: 'text-cyan-500',
    iconName: 'Clock',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'youtube-embed-generator',
    name: 'YouTube Embed Generator',
    description: 'Customize clean responsive iframe embed codes with autoplay, start time, and privacy mode.',
    socialCategory: 'youtube',
    socialCategoryLabel: 'YouTube Tools',
    iconBgColor: 'bg-blue-500/15',
    iconTextColor: 'text-blue-500',
    iconName: 'Code',
    actionType: 'generic'
  },

  // --- INSTAGRAM & TIKTOK (6 Tools) ---
  {
    id: 'instagram-grid-preview',
    name: 'Instagram Grid Preview',
    description: 'Plan and visualize how your images fit together seamlessly in a 3x3 Instagram profile grid layout.',
    socialCategory: 'instagram-tiktok',
    socialCategoryLabel: 'Instagram & TikTok',
    iconBgColor: 'bg-pink-500/15',
    iconTextColor: 'text-pink-500',
    iconName: 'LayoutGrid',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'instagram-caption-formatter',
    name: 'Instagram Caption Formatter',
    description: 'Format Instagram post captions with clean line breaks, spaced paragraphs, and invisible spaces.',
    socialCategory: 'instagram-tiktok',
    socialCategoryLabel: 'Instagram & TikTok',
    iconBgColor: 'bg-fuchsia-500/15',
    iconTextColor: 'text-fuchsia-500',
    iconName: 'AlignLeft',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'instagram-hashtag-formatter',
    name: 'Instagram Hashtag Formatter',
    description: 'Group, clean, and format up to 30 hashtags into neat blocks for Instagram captions or comments.',
    socialCategory: 'instagram-tiktok',
    socialCategoryLabel: 'Instagram & TikTok',
    iconBgColor: 'bg-purple-500/15',
    iconTextColor: 'text-purple-500',
    iconName: 'Hash',
    actionType: 'generic'
  },
  {
    id: 'instagram-bio-formatter',
    name: 'Instagram Bio Formatter',
    description: 'Design multi-line Instagram profile bios with fancy fonts, special symbols, and line spacing.',
    socialCategory: 'instagram-tiktok',
    socialCategoryLabel: 'Instagram & TikTok',
    iconBgColor: 'bg-violet-500/15',
    iconTextColor: 'text-violet-500',
    iconName: 'UserCheck',
    actionType: 'generic'
  },
  {
    id: 'tiktok-caption-counter',
    name: 'TikTok Caption Counter',
    description: 'Track TikTok caption characters against the 4000 character limit and optimize hashtag density.',
    socialCategory: 'instagram-tiktok',
    socialCategoryLabel: 'Instagram & TikTok',
    iconBgColor: 'bg-indigo-500/15',
    iconTextColor: 'text-indigo-500',
    iconName: 'Video',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'tiktok-hashtag-formatter',
    name: 'TikTok Hashtag Formatter',
    description: 'Format trending TikTok hashtags with space-saving syntax for viral video descriptions.',
    socialCategory: 'instagram-tiktok',
    socialCategoryLabel: 'Instagram & TikTok',
    iconBgColor: 'bg-sky-500/15',
    iconTextColor: 'text-sky-500',
    iconName: 'Sparkles',
    actionType: 'generic'
  },

  // --- POSTS & FORMATTING (4 Tools) ---
  {
    id: 'twitter-character-counter',
    name: 'Twitter Character Counter',
    description: 'Count X/Twitter post characters (280 limit), handle link lengths (23 chars), and draft thread posts.',
    socialCategory: 'text-formatters',
    socialCategoryLabel: 'Posts & Formatting',
    iconBgColor: 'bg-sky-500/15',
    iconTextColor: 'text-sky-500',
    iconName: 'Twitter',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'linkedin-formatter',
    name: 'LinkedIn Formatter',
    description: 'Add bold, italic, bullet points, and clean line spacing to increase readability on LinkedIn posts.',
    socialCategory: 'text-formatters',
    socialCategoryLabel: 'Posts & Formatting',
    iconBgColor: 'bg-blue-600/15',
    iconTextColor: 'text-blue-600',
    iconName: 'Linkedin',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'facebook-post-formatter',
    name: 'Facebook Post Formatter',
    description: 'Format long-form Facebook posts with stylish typography, call-to-actions, and bullet lists.',
    socialCategory: 'text-formatters',
    socialCategoryLabel: 'Posts & Formatting',
    iconBgColor: 'bg-blue-500/15',
    iconTextColor: 'text-blue-500',
    iconName: 'Facebook',
    actionType: 'generic'
  },
  {
    id: 'pinterest-pin-size-checker',
    name: 'Pinterest Pin Size Checker',
    description: 'Verify vertical graphic dimensions (2:3 aspect ratio, 1000x1500px) for optimal Pinterest feed placement.',
    socialCategory: 'text-formatters',
    socialCategoryLabel: 'Posts & Formatting',
    iconBgColor: 'bg-red-600/15',
    iconTextColor: 'text-red-600',
    iconName: 'Pin',
    actionType: 'generic'
  },

  // --- EMOJI, HASHTAGS & UTILITIES (7 Tools) ---
  {
    id: 'social-media-calendar',
    name: 'Social Media Calendar',
    description: 'Plan, schedule, and organize weekly content publishing across all social media channels.',
    socialCategory: 'emoji-helpers',
    socialCategoryLabel: 'Emoji & Utilities',
    iconBgColor: 'bg-emerald-500/15',
    iconTextColor: 'text-emerald-500',
    iconName: 'Calendar',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'emoji-picker',
    name: 'Emoji Picker',
    description: 'Browse, filter, and discover categorization of emojis to enhance your posts.',
    socialCategory: 'emoji-helpers',
    socialCategoryLabel: 'Emoji & Utilities',
    iconBgColor: 'bg-amber-500/15',
    iconTextColor: 'text-amber-500',
    iconName: 'Smile',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'emoji-search',
    name: 'Emoji Search',
    description: 'Search emojis instantaneously by keyword, mood, category, or emotion name.',
    socialCategory: 'emoji-helpers',
    socialCategoryLabel: 'Emoji & Utilities',
    iconBgColor: 'bg-orange-500/15',
    iconTextColor: 'text-orange-500',
    iconName: 'Search',
    actionType: 'generic'
  },
  {
    id: 'emoji-copy',
    name: 'Emoji Copy',
    description: 'One-click instant copy tool for popular and trending social media emojis.',
    socialCategory: 'emoji-helpers',
    socialCategoryLabel: 'Emoji & Utilities',
    iconBgColor: 'bg-lime-500/15',
    iconTextColor: 'text-lime-500',
    iconName: 'Copy',
    actionType: 'generic'
  },
  {
    id: 'hashtag-counter',
    name: 'Hashtag Counter',
    description: 'Analyze post text to automatically extract, count, and audit total unique hashtags.',
    socialCategory: 'emoji-helpers',
    socialCategoryLabel: 'Emoji & Utilities',
    iconBgColor: 'bg-teal-500/15',
    iconTextColor: 'text-teal-500',
    iconName: 'Hash',
    actionType: 'generic'
  },
  {
    id: 'unicode-emoji-converter',
    name: 'Unicode Emoji Converter',
    description: 'Convert emojis to HTML entity codes, UTF-8 hex representations, and shortcodes.',
    socialCategory: 'emoji-helpers',
    socialCategoryLabel: 'Emoji & Utilities',
    iconBgColor: 'bg-purple-500/15',
    iconTextColor: 'text-purple-500',
    iconName: 'Binary',
    actionType: 'generic'
  },
  {
    id: 'qr-code-generator',
    name: 'QR Code Generator',
    description: 'Create custom branded QR codes for social profiles, bio links, Wi-Fi, and website URLs.',
    socialCategory: 'emoji-helpers',
    socialCategoryLabel: 'Emoji & Utilities',
    iconBgColor: 'bg-indigo-500/15',
    iconTextColor: 'text-indigo-500',
    iconName: 'QrCode',
    isPopular: true,
    actionType: 'generic'
  }
];
